/**
 * Les URL des rendus d'une image À NOUS — le miroir front de `ImageVariants` côté back.
 *
 * Deux générations d'images cohabitent (feature 0067) et se distinguent À L'URL SEULE :
 *
 *  - **Une image 0067** est un dossier : `…/api/assets/public/assets/<store>/<mois>/<uuid>/full.<jpg|png|webp>`
 *    est le fichier pleine taille (les propres pixels du vendeur), et ses rendus sont des objets
 *    voisins, `<dossier>/w<px>.avif` et `<dossier>/w<px>.webp`, taillés une seule fois depuis
 *    l'original et servis par l'hôte media sans passer par la JVM. On les demande par leur URL
 *    explicite : c'est ce qui permet au bord (Cloudflare) de les mettre en cache, et à `<picture>`
 *    d'offrir l'AVIF avec le WebP en repli sans négociation `Accept`.
 *  - **Une image d'avant 0067** est un WebP plat `…/<uuid>.webp` sans original : on lui demande une
 *    largeur en `?w=`, que le backend découpe et met en cache. Rien ne change pour elle.
 *
 * Une image étrangère (URL collée par la vendeuse, CDN d'un fournisseur) ou une URL déjà paramétrée est
 * rendue telle quelle, et `undefined` pour un `srcset` : React omet alors l'attribut et le `src` seul
 * se comporte exactement comme avant.
 */
const PUBLIC_PATH = '/api/assets/public/';
const FAMILY_SUFFIX = /\/full\.(?:jpg|png|webp)$/;
/**
 * Les familles qui ont une échelle AVIF : celles dont la source est une PHOTO par nature (JPEG, HEIC →
 * `full.jpg` / `full.webp`). Une source PNG ou GIF (`full.png`) est classée par le serveur — logo ou
 * photo exportée en PNG — et un logo n'a QUE des WebP sans perte, pixel-identiques ; lui demander un
 * AVIF serait demander un encodage lossy d'un graphisme. Le front ne le fait donc jamais.
 */
const PHOTO_FAMILY_SUFFIX = /\/full\.(?:jpg|webp)$/;
/** Une de nos URL de livraison, sans paramètre ni fragment. */
export function isOurAsset(url) {
    return !!url && !url.includes('?') && !url.includes('#') && url.includes(PUBLIC_PATH);
}
/** Une image 0067 (un dossier avec ses rendus explicites), reconnue à son `/full.<ext>`. */
export function isRenditionFamily(url) {
    return isOurAsset(url) && FAMILY_SUFFIX.test(url);
}
function familyStem(url) {
    return url.replace(FAMILY_SUFFIX, '');
}
/** Un WebP plat d'avant 0067 — la seule forme que `?w=` sait redimensionner. */
function isLegacyWebp(url) {
    return isOurAsset(url) && url.endsWith('.webp') && !FAMILY_SUFFIX.test(url);
}
/**
 * Les largeurs que le backend sait découper pour une image D'AVANT 0067 — miroir exact de
 * `ImageVariants.WIDTHS`. Le serveur ARRONDIT toute demande à l'une d'elles ; demander une valeur hors
 * liste ne coûte donc pas un fichier de plus, mais une URL de plus — et une URL de plus, c'est une entrée
 * de cache de plus dans le navigateur, donc les MÊMES octets retéléchargés. D'où [snapVariantWidth].
 */
export const ASSET_VARIANT_WIDTHS = [160, 320, 480, 640, 960, 1280, 1600];
/**
 * Les largeurs des rendus d'une image 0067 — miroir exact de `ImageVariants.RENDITION_WIDTHS`. 2048 est
 * le nouveau palier du haut : l'original n'est plus plafonné à 2560, et une colonne média de bureau sur
 * écran 2× avec le zoom au survol (×1,6) peint réellement ~2 100 px.
 */
export const ASSET_RENDITION_WIDTHS = [320, 480, 640, 960, 1280, 1600, 2048];
/** Le plus petit palier D'AVANT 0067 ≥ la largeur voulue, sinon le plus grand. Même règle que le serveur. */
export function snapVariantWidth(wanted) {
    const last = ASSET_VARIANT_WIDTHS[ASSET_VARIANT_WIDTHS.length - 1] ?? 1600;
    if (!Number.isFinite(wanted))
        return last;
    return ASSET_VARIANT_WIDTHS.find((w) => w >= wanted) ?? last;
}
/** Le plus petit palier 0067 ≥ la largeur voulue, sinon le plus grand. Même règle que le serveur. */
export function snapRenditionWidth(wanted) {
    const last = ASSET_RENDITION_WIDTHS[ASSET_RENDITION_WIDTHS.length - 1] ?? 2048;
    if (!Number.isFinite(wanted))
        return last;
    return ASSET_RENDITION_WIDTHS.find((w) => w >= wanted) ?? last;
}
/**
 * L'URL WebP d'une image à nous, bornée à `width` : le rendu explicite d'une image 0067 (largeur
 * arrondie au palier), ou `?w=` pour un WebP d'avant. Une URL qui n'est pas à nous revient telle quelle.
 */
export function assetVariantUrl(url, width) {
    if (!Number.isFinite(width) || width <= 0)
        return url;
    if (isRenditionFamily(url))
        return `${familyStem(url)}/w${snapRenditionWidth(width)}.webp`;
    if (isLegacyWebp(url))
        return `${url}?w=${Math.round(width)}`;
    return url;
}
/**
 * L'URL AVIF d'une image 0067 à `width` — `undefined` pour tout le reste : une image d'avant 0067 n'a
 * pas d'AVIF (le backend n'en découpe pas depuis un WebP déjà lossy), une image étrangère non plus.
 */
export function assetAvifUrl(url, width) {
    if (!Number.isFinite(width) || width <= 0)
        return undefined;
    if (!isRenditionFamily(url) || !PHOTO_FAMILY_SUFFIX.test(url))
        return undefined;
    return `${familyStem(url)}/w${snapRenditionWidth(width)}.avif`;
}
/**
 * The width a gallery THUMBNAIL asks for. The thumb box is ≤ 64 CSS px on every layout (mobile strip and
 * desktop vertical strip alike, the latter is 10–30 % of a ~500 px column); 160 covers a 2.5× DPR screen
 * without asking the phone to download 493–823 px originals for 50 px boxes (≈224 KB wasted on the
 * reference PDP — Lighthouse 2026-09-11). Une image 0067 n'a pas de palier 160 : elle rend son 320, qui
 * pèse quelques kilo-octets en AVIF/WebP d'une seule génération.
 */
export const THUMBNAIL_VARIANT_WIDTH = 160;
/**
 * Les largeurs d'une PHOTO D'AVIS, et la taille qu'elle occupe une fois peinte.
 *
 * Elle n'est plus une vignette de 72 px : depuis le 16/09/2026 la photo d'une cliente est peinte sur
 * TOUTE la largeur de sa carte, sur téléphone comme sur bureau — « les gens vont donner plus
 * d'importance à l'image qu'au texte » (fondatrice). La carte mesure ~320 px dans la maçonnerie à trois
 * colonnes et ~92 % de l'écran sur téléphone, d'où ce `sizes` : un téléphone télécharge le palier 320 ou
 * 480, jamais le 960 que réclamerait un grand écran. Sans lui, `srcset` choisirait la plus grande.
 */
export const REVIEW_PHOTO_VARIANT_WIDTHS = [320, 480, 640, 960];
/** Le repli `src` : le palier qui couvre une carte de 320 px sur un écran 2×. */
export const REVIEW_PHOTO_VARIANT_WIDTH = 640;
/** La largeur PEINTE de la photo, miroir exact de la maçonnerie (3 colonnes → 2 → 1). */
export const REVIEW_PHOTO_SIZES = '(min-width:990px) 340px, (min-width:600px) 46vw, 92vw';
/**
 * The widths a PRODUCT PHOTO is offered in. Every one of them exists on BOTH backend ladders except
 * 2048, which only a 0067 image has (an image from before is filtered to its own ladder, so a `w`
 * descriptor never describes a file the browser did not receive).
 *
 * Why 2048 and not the stored size: the biggest a product photo is ever PAINTED is the desktop media
 * column — 56 % of a 1200px container ≈ 672 CSS px, ×1.6 on hover zoom, ×2 on a Retina screen ≈ 2150
 * physical px. The full-size file stays reachable as the `src` fallback and is what the lightbox opens;
 * it is simply not what a page load should download.
 */
export const GALLERY_VARIANT_WIDTHS = [480, 640, 960, 1280, 1600, 2048];
/**
 * What the browser is told about the PAINTED size of a gallery image, so it can pick a width before any
 * layout exists. Mirrors the gallery's own CSS:
 *   - below 750px the slide is `flex:0 0 100%` of a carousel that BLEEDS through the section gutter → 100vw;
 *   - above it, the image fills the media column (`media_size` 42/48/56 % of the content width) → ~58vw;
 *   - past the 1200px container cap that column stops growing, so a fixed 700px is the honest answer
 *     (58vw of a 2560px monitor would be 1485px of photo for a 672px box).
 * Slightly generous on purpose: over-asking costs a step of bytes, under-asking costs visible softness on
 * the one image the shop sells with.
 */
/** Largeur peinte de l'image dans la colonne média, une fois le conteneur à son plafond de 1200px. */
const MEDIA_COLUMN_PX = 700;
/** La même colonne, exprimée en part de fenêtre, entre 750px et le plafond du conteneur. */
const MEDIA_COLUMN_VW = 58;
/**
 * [GALLERY_IMAGE_SIZES], mais en tenant compte du ZOOM AU SURVOL quand la vendeuse l'a activé.
 *
 * Pourquoi ce n'est pas un détail : `transform:scale(1.6)` peint réellement l'image 1,6 fois plus grande.
 * `sizes` décrit la taille PEINTE — si on annonce la taille de la boîte au repos, le navigateur choisit un
 * fichier de 960px que le survol étire ensuite à 1120px. C'est un AGRANDISSEMENT, et il se voit : mesuré
 * sur une photo de broderie (2026-09-15), les fils deviennent visiblement mous là où l'original restait
 * net. Le zoom existe précisément pour inspecter la matière ; le rendre flou le viderait de son sens.
 *
 * Seules les bornes BUREAU sont multipliées : `:hover` n'existe pas sur un écran tactile, et sur téléphone
 * l'appui ouvre la visionneuse plein écran, qui charge l'original sans `srcset`. Gonfler la borne mobile
 * ferait payer des octets pour une interaction qui n'a pas lieu.
 */
export function galleryImageSizes(hoverZoomScale = 1) {
    const scale = Number.isFinite(hoverZoomScale) && hoverZoomScale > 1 ? hoverZoomScale : 1;
    const px = Math.round(MEDIA_COLUMN_PX * scale);
    const vw = Math.round(MEDIA_COLUMN_VW * scale);
    return `(min-width:1200px) ${px}px, (min-width:750px) ${vw}vw, 100vw`;
}
/** Les largeurs réellement offertes pour cette URL, dans l'ordre, sans doublon. */
function offeredWidths(url, widths) {
    if (isRenditionFamily(url)) {
        return [...new Set(widths.map(snapRenditionWidth))].sort((a, b) => a - b);
    }
    return widths.filter((w) => ASSET_VARIANT_WIDTHS.includes(w));
}
/**
 * The WebP `srcset` for one of our stored product photos — `<url>?w=480 480w, …` for an image from
 * before 0067, `<dossier>/w480.webp 480w, …` for a 0067 image — or `undefined` when the URL is not ours
 * to resize (an external image a seller pasted, a video poster, an already-parameterised URL).
 * `undefined` rather than a one-entry set on purpose: React then omits the attribute entirely and the plain
 * `src` behaves exactly as it did before, so a foreign image is untouched by this whole mechanism.
 *
 * Note the backend never UPSCALES: a photo narrower than a step is served at its own size under that step's
 * key, so offering 2048 for a small image is harmless (the browser gets the full-size bytes back).
 */
export function assetVariantSrcSet(url, widths = GALLERY_VARIANT_WIDTHS) {
    if (!isRenditionFamily(url) && !isLegacyWebp(url))
        return undefined;
    const offered = offeredWidths(url, widths);
    if (offered.length === 0)
        return undefined;
    return offered.map((w) => `${assetVariantUrl(url, w)} ${w}w`).join(', ');
}
/**
 * Le `srcset` AVIF d'une image 0067, pour la `<source type="image/avif">` d'un `<picture>` — les mêmes
 * largeurs et les mêmes descripteurs que [assetVariantSrcSet], sur les fichiers `.avif` voisins.
 * `undefined` pour une image d'avant 0067, une source PNG/GIF (`full.png`, qui peut être un graphisme
 * sans AVIF) ou une image étrangère : la `<source>` n'est alors pas rendue.
 */
export function assetAvifSrcSet(url, widths = GALLERY_VARIANT_WIDTHS) {
    if (!isRenditionFamily(url) || !PHOTO_FAMILY_SUFFIX.test(url))
        return undefined;
    const offered = offeredWidths(url, widths);
    if (offered.length === 0)
        return undefined;
    return offered.map((w) => `${assetAvifUrl(url, w)} ${w}w`).join(', ');
}
/**
 * Les réglages d'une galerie → la valeur `sizes` que son `<img>` portera.
 *
 * UNE seule source de vérité, parce que DEUX endroits doivent tomber sur la même chaîne : le composant qui
 * peint l'image, et le `<link rel="preload">` du `<head>` qui la demande avant lui. S'ils divergent, le
 * navigateur précharge un fichier puis en télécharge un autre — la photo voyage DEUX fois, ce que le
 * préchargement était censé éviter.
 *
 * Les valeurs par défaut reproduisent celles du bloc (`imageZoomDesktop: 'combined'`, `hoverZoomStyle:
 * 'inline'`), donc une galerie qui n'a jamais été réglée rend exactement ce que rend le composant.
 */
export function galleryPaintedSizes(settings) {
    const mode = typeof settings.imageZoomDesktop === 'string' ? settings.imageZoomDesktop : 'combined';
    const zoom = settings.zoom !== false && mode !== 'none' && mode !== 'lightbox';
    if (!zoom)
        return galleryImageSizes();
    const pane = settings.hoverZoomStyle === 'pane';
    return galleryImageSizes(pane ? HOVER_ZOOM_PANE : HOVER_ZOOM_INLINE);
}
/** Facteurs de `transform:scale` de la règle `__zoom:hover` — ils DOIVENT rester alignés sur ce CSS. */
export const HOVER_ZOOM_PANE = 1.6;
export const HOVER_ZOOM_INLINE = 1.08;
/**
 * La taille peinte AU REPOS — ce qu'une image de galerie occupe quand le zoom au survol est coupé, et ce
 * que vaut une image produit hors galerie. [galleryPaintedSizes] est ce qu'il faut appeler quand on a les
 * réglages du bloc sous la main ; cette constante est le repli.
 */
export const GALLERY_IMAGE_SIZES = galleryImageSizes();
/**
 * Les attributs `src`/`srcSet` d'une image dont la largeur PEINTE est connue et FIXE — un logo, un
 * avatar, une icône, une vignette de panier.
 *
 * Pourquoi (mesuré sur la page produit de la fondatrice, Lighthouse 17/10/2026) : 101 des `<img>` de nos
 * thèmes rendaient `src={image.url}` tel quel, c'est-à-dire le fichier d'ORIGINE. Deux logos peints à
 * 90 px dans le tableau comparatif pesaient 111 Ko et 82 Ko ; le logo d'en-tête 21 Ko ; l'image de pied
 * de page 26 Ko. 278 Ko pour des boîtes de 90 à 120 px, sur une page qui en téléchargeait 1,43 Mo au
 * total. `unitVariants.ts` avait déjà réglé exactement ce défaut pour les pastilles de variante le
 * 29/09/2026 — il n'avait simplement jamais été généralisé.
 *
 * Des descripteurs `x`, pas `w` : la boîte ne change pas de taille avec la fenêtre, donc il n'y a aucune
 * `sizes` honnête à écrire, et un descripteur `w` sans `sizes` vaut `100vw` — l'erreur exacte qu'on
 * cherche à éviter. Les paliers sont arrondis par le serveur, donc 1×/2×/3× retombent souvent sur le
 * même fichier : on déduplique, et un seul palier rend `srcSet` inutile (React omet alors l'attribut et
 * l'image se comporte comme avant).
 *
 * Une image étrangère (URL collée par la vendeuse) ou une largeur absurde revient INTACTE : ce mécanisme
 * ne doit jamais pouvoir casser une image qu'il ne sait pas redimensionner.
 */
export function fixedWidthImgProps(url, cssWidth) {
    if (!url || !Number.isFinite(cssWidth) || cssWidth <= 0)
        return { src: url };
    if (!isRenditionFamily(url) && !isLegacyWebp(url))
        return { src: url };
    const snap = isRenditionFamily(url) ? snapRenditionWidth : snapVariantWidth;
    // 2× d'abord : c'est le `src` de repli, et la densité de la quasi-totalité des téléphones.
    const rungs = [1, 2, 3].map((dpr) => snap(Math.round(cssWidth * dpr)));
    const src = assetVariantUrl(url, rungs[1]);
    // Un palier porte le PLUS GRAND des `x` qu'il couvre, pas le plus petit : 90 px arrondit 1× et 2× au
    // même fichier, et l'étiqueter « 1x » enverrait un écran 2× chercher le palier du dessus pour rien.
    const byRung = new Map();
    rungs.forEach((w, i) => byRung.set(w, i + 1));
    if (byRung.size < 2)
        return { src };
    return {
        src,
        srcSet: [...byRung]
            .sort((a, b) => a[0] - b[0])
            .map(([w, dpr]) => `${assetVariantUrl(url, w)} ${dpr}x`)
            .join(', '),
    };
}
//# sourceMappingURL=assetVariant.js.map